import React, {useEffect, useState, useMemo, useRef, useCallback} from 'react';
import ArrAccordionWithCheckbox from '../../common/arr-accordion-with-checkbox/ArrAccordionWithCheckbox';
import FilterHeader, {ReportPayload} from './FilterHeader';
import {
  useGetExplorer,
  useCommunicationData,
  useBulkCommunicationReport,
  useCommunicationBulkReport,
} from '../../../hooks/communicationExplorer/useCommunicationExplorer';
import {useDispatch} from 'react-redux';
import {
  setDocListBackNavigation,
  setDocListId,
} from '../../../redux/slice/docListSlice';
import {
  storeDocumentListMasterId,
  storeSourceTypeCode,
} from '../../../redux/slice/docViewSlice';
import {setTimelineIngestion} from '../../../redux/slice/timelineGapReportSlice';
import {clearDocViewState} from '../../document-viewer/docViewerUtils';
import BubblesAndLinks from '../../../pages/reports/communication-explorer/buble-node/Index';
import {Box} from '@mui/material';
import CommunicationsExplorerHeader from './commnExplorerHeader/CommunicationsExplorerHeader';
import NoReport from '../../../pages/reports/communication-explorer/NoReport';
import {communicationExpUrl, conceptExpUrl, svgToPdfDownload} from './utils';
import ArrToastNotification from '../../common/arr-toast-notification/ArrToastNotification';
import {
  ToastType,
  SourceTypeCode,
  ReportActionMenuOptionTypes,
  RoleId,
} from '../../../shared/constants';
import {t} from 'i18next';
import {CustomMenuItem} from '../../common/arr-menu/ArrMenu';
import {useSelector} from 'react-redux';
import {RootState} from '../../../redux/store';
import ArrDocMaxLimitWarning from '../../common/arr-doc-max-limit-warning/ArrDocMaxLimitWarning';
import {
  IFlowChart,
  IFlowChartNode,
  IFlowChartLink,
  IConnector,
  IExchangeDocs,
  ICommunicationLiteralValue,
  ICommunicationDataRequest,
  ICommunicationDataFilter,
  IBulkCommunicationReportRequest,
} from '../../../api/endpoints/communicationExplorerApi';
import {
  INode,
  ILink,
} from '../../../pages/reports/communication-explorer/buble-node/type';
import {
  useCallBulkActionsPopup,
  useCallBulkActionsLikePopup,
  useDoAction,
  useGetBulkAssignDocListId,
} from '../../../hooks/assignments/useAssignments';
import BulkTagUntag from '../../action-menu/bulk-tag-untag/BulkTagUntag';
import BulkTaggingWarning from '../../action-menu/bulk-tagging-warning/BulkTaggingWarning';
import BulkReleaseFlyout from '../../action-menu/bulk-release-flyout/BulkReleaseFlyout';
import BulkAssignFlyout from '../../action-menu/bulk-assign-flyout/BulkAssignFlyout';
import BulkFolderUnFolder from '../../action-menu/bulk-folder-unfolder/BulkFolderUnFolder';
import ActionLikeDocuments, {
  IActionListItem,
} from '../../action-menu/action-like-documents/ActionLikeDocuments';
import ArrLoadingSpinner from '../../common/arr-loading-spinner/ArrLoadingSpinner';
import {
  IBulkActionPopupResponse,
  IBulkActionLikePopupResponse,
  IBulkAssignDocListIdRequest,
  IBulkAssignDocListIdResponse,
} from '../../../api/types/assignment.type';
import {useSearchParams, useNavigate} from 'react-router-dom';
import {
  clearReportBackNavigation,
  setReportDetails,
} from '../../../redux/slice/comExplorerSlice';

export type ReportNotificationType = {
  notificationId: number;
  reportId: number;
};

type SelectedSourceMap = Record<string, any[] | null>;

type ActionSelection = {
  type: 'new' | 'existing';
  ids: string | string[];
};

const initialActionList: IActionListItem[] = [
  {
    id: 'family_members',
    label: 'Family Members',
    isLoading: false,
    count: 0,
    selected: false,
  },
  {
    id: 'email_docs',
    label: 'Email threaded docs',
    isLoading: false,
    count: 0,
    selected: false,
  },
  {
    id: 'near_duplicates',
    label: 'Near Duplicates',
    isLoading: false,
    count: 0,
    selected: false,
  },
];

const CommunicationExplorer = () => {
  const [reqData, setReqData] = useState<ReportNotificationType | null>(null);
  const [explorerDataList, setexplorerDataList] = useState<any>(null);
  const [selectedSourceMap, setSelectedSourceMap] = useState<SelectedSourceMap>(
    {}
  );
  const [showBy, setShowBy] = useState<string | number | null>(null);
  const [reportCriteriaForSave, setReportCriteriaForSave] =
    useState<ReportPayload | null>(null);
  const [dataAsOfForSave, setDataAsOfForSave] = useState<string>('');
  const [canDownload, setCanDownload] = useState<boolean>(false);
  const [displayToast, setDisplayToast] = useState<boolean>(false);
  const [searchParams] = useSearchParams();
  const reportIDFromUrl = Number(searchParams.get('ReportID'));
  const reportTitle = searchParams.get('ReportLabel') || '';
  const {mutate: ExplorerViewData} = useGetExplorer();

  // Add state for selected nodes and links, and current report data
  const [selectedNodeIds, setSelectedNodeIds] = useState<Set<number>>(
    new Set()
  );
  const [selectedLinkIds, setSelectedLinkIds] = useState<Set<string>>(
    new Set()
  );
  const [currentReportData, setCurrentReportData] = useState<{
    node: INode[];
    link: ILink[];
    exchangeDocs: any[];
  } | null>(null);

  // Get user role
  const {roleId, conceptSourceId} = useSelector((state: RootState) => ({
    roleId: state.authenticatedUser.user?.roleId,
    conceptSourceId: state.comExplorer.sourceId,
  }));

  // Add refs to avoid closure issues
  const reqDataRef = useRef<ReportNotificationType | null>(null);
  const reportCriteriaRef = useRef<ReportPayload | null>(null);
  const currentReportDataRef = useRef<{
    node: INode[];
    link: ILink[];
    exchangeDocs: any[];
  } | null>(null);
  const selectedNodeIdsRef = useRef<Set<number>>(new Set());
  // Ref to store base request for navigation (used after warning modal confirmation)
  const baseRequestForNavigationRef = useRef<ICommunicationDataRequest | null>(
    null
  );

  useEffect(() => {
    reqDataRef.current = reqData;
  }, [reqData]);

  useEffect(() => {
    reportCriteriaRef.current = reportCriteriaForSave;
  }, [reportCriteriaForSave]);

  // Ref to track if report criteria has been populated from API response
  const hasPopulatedCriteriaRef = useRef<boolean>(false);

  // Reset the populated flag when report ID changes
  useEffect(() => {
    hasPopulatedCriteriaRef.current = false;
  }, [reportIDFromUrl]);

  useEffect(() => {
    currentReportDataRef.current = currentReportData;
  }, [currentReportData]);

  useEffect(() => {
    selectedNodeIdsRef.current = selectedNodeIds;
  }, [selectedNodeIds]);

  // Bulk action state
  const [masterDocListId, setMasterDocListId] = useState<number>(0);
  const [isBulkTagAction, setIsBulkTagAction] = useState<boolean>(false);
  const [isBulkWarning, setIsBulkWarning] = useState<boolean>(false);
  const [selectedNewExistingTag, setSelectedNewExistingTag] =
    useState<ActionSelection | null>(null);
  const [selectedNewExistingFolder, setSelectedNewExistingFolder] =
    useState<ActionSelection | null>(null);
  const [bulkSelectedAction, setBulkSelectedAction] = useState<string>('');
  const [actionLikeChangeList, setActionLikeChangeList] =
    useState<IActionListItem[]>();
  const [isActionLikeModal, setIsActionLikeModal] = useState<boolean | string>(
    false
  );
  const [isBulkAssignAction, setIsBulkAssignAction] = useState<boolean>(false);
  const [isBulkFolderAction, setIsBulkFolderAction] = useState<boolean>(false);
  const [_bulkAssignmentId, setBulkAssignmentId] = useState<number>(0);
  const [selectedTreeIds, setSelectedTreeIds] = useState<string[]>([]);
  const [isBulkReleaseAction, setIsBulkReleaseAction] =
    useState<boolean>(false);
  const [securityGroupId, setSecurityGroupId] = useState<string[]>([]);
  const [isNavigatingToDocList, setIsNavigatingToDocList] =
    useState<boolean>(false);
  const [isNavigatingToDocView, setIsNavigatingToDocView] =
    useState<boolean>(false);
  const [showDocLimitWarning, setShowDocLimitWarning] =
    useState<boolean>(false);
  const [navigationType, setNavigationType] = useState<SourceTypeCode>();
  const [selectedActionId, setSelectedActionId] = useState<string>('');
  const [documentCountFromAPI, setDocumentCountFromAPI] = useState<number>(0);

  const {
    communicationStateValue,
    sourceId: communicationSourceId,
    backSourcePath,
    communicationShowByFilters,
  } = useSelector((state: RootState) => state.comExplorer);

  // Navigation hooks
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // Hooks for bulk actions
  const {mutate: postCommunicationData, isPending: isCommunicationDataLoading} =
    useCommunicationData();

  const {isPending: isBulkActionsPopupLoading} = useCallBulkActionsPopup(
    (response: IBulkActionPopupResponse) => {
      setMasterDocListId(response.masterDocListID);
    },
    (_error) => {
      setDisplayToast(true);
    }
  );

  const {mutate: callBulkActionsLikePopup} = useCallBulkActionsLikePopup(
    (response: IBulkActionLikePopupResponse) => {
      setMasterDocListId(response.masterDocListID);
    },
    (_error) => {
      setDisplayToast(true);
    }
  );

  const {mutate: doAction} = useDoAction(
    () => {
      setIsBulkWarning(false);
      setIsBulkTagAction(false);
      setDisplayToast(true);
    },
    (_error) => {
      setDisplayToast(true);
    }
  );

  // Refs for bulk assign
  const pendingAssignAction = useRef<string>('');
  const assignmentSelectedTab = useRef<'existing' | 'new'>('existing');
  const persistContentForAssign = useRef<boolean>(false);
  const samplingMethodForAssign = useRef<string>('count');
  const countForAssign = useRef<number>(1);
  const percentForAssign = useRef<number>(1);
  const selectedTreeIdsRef = useRef<string[]>([]);

  useEffect(() => {
    selectedTreeIdsRef.current = selectedTreeIds;
  }, [selectedTreeIds]);

  const {mutate: getBulkAssignDocListId} = useGetBulkAssignDocListId(
    (response: IBulkAssignDocListIdResponse) => {
      const newMasterDocListId = response.masterDocListID;
      const assignmentId = response.assignmentId;
      setMasterDocListId(newMasterDocListId);
      setBulkAssignmentId(assignmentId);

      if (pendingAssignAction.current === 'assign_documents') {
        callBulkActionsLikePopupApiCall(
          'ASSIGN',
          selectedTreeIdsRef.current,
          pendingAssignAction.current,
          newMasterDocListId,
          () => {
            setIsActionLikeModal('bulkAssign');
          },
          persistContentForAssign.current
        );
      }
    },
    (_error) => {
      setDisplayToast(true);
    }
  );

  // Hook for bulk communication report
  const {mutate: bulkCommunicationReport, isPending: isBulkReportLoading} =
    useBulkCommunicationReport();

  const {
    mutate: communicationBulkReport,
    // isPending: isCommunicationBulkReportLoading,
  } = useCommunicationBulkReport();

  const requestPayload = useMemo(
    () => ({
      reportID: reportIDFromUrl.toString() ?? '',
      reportLabel: '',
      isDeepDive: true,
      preReportID: '',
      pageSource: '',
      docCommuMapReportID: '',
      navigationSourceType: '',
      source: '',
      previousUrl: '',
      reportMasterDoclistId: '',
      flowChartWithSelections: '',
      redirectionData: [{id: 'string', text: 'string'}],
      docsSessionObject: [{id: 'string', text: 'string'}],
      searchId: 0,
      projectName: '',
      userId: 0,
    }),
    [reportIDFromUrl]
  );

  const GetInitialData = useCallback(() => {
    ExplorerViewData(requestPayload, {
      onSuccess: (data) => {
        setexplorerDataList(data);
      },
      onError: () => {},
    });
  }, [ExplorerViewData, requestPayload]);

  const handleReportRunSuccess = (payload: ReportPayload, dataAsOf: string) => {
    setReportCriteriaForSave(payload);
    setDataAsOfForSave(dataAsOf);
  };

  useEffect(() => {
    GetInitialData();
  }, [GetInitialData]);

  useEffect(() => {
    return () => {
      const currentUrl =
        `${window.location.pathname}${window.location.search}`.toLowerCase();

      if (
        currentUrl.includes(communicationExpUrl.toLowerCase()) ||
        currentUrl.includes(conceptExpUrl.toLowerCase())
      )
        return;

      if (conceptSourceId) {
        dispatch(clearReportBackNavigation());
      }
    };
  }, []);

  const handleSourceSelectionsUpdate = (map: SelectedSourceMap) => {
    setSelectedSourceMap(map);

    const selectedSource = Object.keys(map)[0];
    const sourceDetails =
      selectedSource && map[selectedSource] ? map[selectedSource]![0] : null;

    if (!sourceDetails) {
      return;
    }

    setReportCriteriaForSave((prev) => {
      if (!prev) {
        return null;
      }

      return {
        ...prev,
        sourceName: sourceDetails?.sourceName ?? prev.sourceName,
        source: sourceDetails?.source ?? prev.source,
        navigationSourceType:
          sourceDetails?.navigationSourceType ?? prev.navigationSourceType,
        dateAsOf: dataAsOfForSave || prev.dateAsOf,
      };
    });
  };

  const hasSelectedSources = useMemo(() => {
    return Object?.values(selectedSourceMap)?.some((selections) => {
      const sourceIds = selections?.[0]?.source;
      return Array.isArray(sourceIds) && sourceIds.length > 0;
    });
  }, [selectedSourceMap]);

  const resetSourceIds = () => {
    const sourceIDArray = explorerDataList?.commMap?.sourceID;
    if (Array.isArray(sourceIDArray) && sourceIDArray.length > 0) {
      if (explorerDataList && explorerDataList.commMap) {
        setexplorerDataList((prev: any) => ({
          ...prev,
          commMap: null,
        }));
      }
    }
  };

  const sections = [
    {
      id: 'filterHeader',
      title: {
        primaryText: 'Select Sources & Set Display Controls',
        secondaryText: '',
      },
      content: (
        <FilterHeader
          setReqData={setReqData}
          explorerData={explorerDataList}
          onSourceSelectionsUpdate={handleSourceSelectionsUpdate}
          setShowBy={setShowBy}
          onReportRunSuccess={handleReportRunSuccess}
          onClearSourceId={resetSourceIds}
        />
      ),
      checked: false,
      showCheckbox: false,
    },
  ];

  const svgRef = useRef<SVGSVGElement | null>(null);

  const handleShareReport = () => {
    if (canDownload) {
      svgToPdfDownload(svgRef);
    } else {
      setDisplayToast(true);
    }
  };

  // Memoized handlers for BubblesAndLinks to prevent unnecessary re-renders
  // These callbacks are stable and won't cause BubblesAndLinks to re-render
  const handleNodeSelectionChange = useCallback((nodeIds: number[]) => {
    setSelectedNodeIds(new Set(nodeIds));
  }, []);

  const handleLinkSelectionChange = useCallback((linkIds: string[]) => {
    setSelectedLinkIds(new Set(linkIds));
  }, []);

  const handleDataLoad = useCallback(
    (data: {node: INode[]; link: ILink[]; exchangeDocs: any[]}) => {
      setCurrentReportData(data);
    },
    []
  );

  // Handler to extract report criteria from API response when page loads with report ID
  const handleReportDataLoad = useCallback(
    (fullData: any) => {
      // Only populate if we have report ID from URL and haven't already populated
      if (reportIDFromUrl && fullData && !hasPopulatedCriteriaRef.current) {
        // Check if reportCriteriaForSave is empty or missing key values using ref
        const currentCriteria = reportCriteriaRef.current;
        const needsUpdate =
          !currentCriteria ||
          !currentCriteria.showByCount ||
          !currentCriteria.showBy ||
          !currentCriteria.showByVolume ||
          !currentCriteria.sourceName ||
          currentCriteria.source.length === 0;

        if (needsUpdate) {
          // Try multiple possible data locations - the API response structure might vary
          // Check: fullData directly, fullData.commMap, fullData.data, fullData.selectionCriteria, or nested structures
          const dataSource =
            fullData.selectionCriteria ||
            fullData.commMap ||
            fullData.data ||
            fullData.response ||
            fullData;

          // Extract filters from filterByMetaData - map IFilterByMetaData to FilterItem format
          // Note: filterByMetaData has different structure, so we'll create basic filter items
          const filterByMetaData = dataSource.filterByMetaData || [];
          const filters: ReportPayload['filters'] = filterByMetaData.map(
            (metaData: any) => ({
              projectFieldId: metaData.fieldID || '',
              projectFieldName: metaData.fieldName || '',
              dataType: metaData.fieldType || '',
              projectFieldLabel: metaData.fieldName || '',
              type: '0', // Default type
              operand: '',
              values: metaData.literalValue ? [metaData.literalValue] : [],
              metaDataID: 0,
              text: metaData.literalValue || '',
            })
          );

          // Extract values from API response - check multiple possible property names and locations
          // Also check if values are in selectionCriteria (saved report metadata) or explorerDataList.commMap
          const showByCount =
            dataSource.showByCount ||
            fullData.showByCount ||
            explorerDataList?.commMap?.showByCount ||
            communicationShowByFilters?.topValue ||
            null;
          const showBy =
            dataSource.showBy ||
            fullData.showBy ||
            explorerDataList?.commMap?.showBy ||
            communicationShowByFilters?.categoryValue ||
            null;
          const showByVolume =
            dataSource.showByVolume ||
            fullData.showByVolume ||
            explorerDataList?.commMap?.showByVolume ||
            communicationShowByFilters?.volumeValue ||
            null;
          const sourceType =
            dataSource.sourceType ||
            fullData.sourceType ||
            explorerDataList?.commMap?.sourceType ||
            '';
          const sourceID =
            dataSource.sourceID ||
            dataSource.source ||
            fullData.sourceID ||
            fullData.source ||
            explorerDataList?.commMap?.sourceID ||
            [];
          const navigationSourceType =
            dataSource.navigationSourceType ||
            fullData.navigationSourceType ||
            explorerDataList?.commMap?.navigationSourceType ||
            '';
          const dataAsOf =
            dataSource.dataAsOf ||
            fullData.dataAsOf ||
            explorerDataList?.commMap?.dataAsOf ||
            '';

          // Build ReportPayload from API response - even if some values are empty
          // This ensures reportCriteriaForSave is set so buildCommunicationDataRequest can work
          const payload: ReportPayload = {
            sourceName: sourceType,
            source: Array.isArray(sourceID) ? sourceID : [],
            navigationSourceType: navigationSourceType,
            filters: filters,
            showByCount: showByCount,
            showBy: showBy,
            showByVolume: showByVolume,
            isDeepDive: false,
            prevReportId: '0',
            deepDiveFlowChart: null,
          };

          setReportCriteriaForSave(payload);
          setDataAsOfForSave(dataAsOf);

          // Set showBy state if available
          if (showBy) {
            setShowBy(showBy);
          }

          // Mark as populated to prevent future updates
          hasPopulatedCriteriaRef.current = true;
        }
      }
    },
    [reportIDFromUrl, explorerDataList, communicationShowByFilters]
  );

  // Helper function to build flowChart from selected nodes (memoized)
  // Must be defined before buildCommunicationDataRequest
  const buildFlowChartFromSelectedNodes = useCallback(
    (
      reportData: {node: INode[]; link: ILink[]; exchangeDocs: any[]},
      selectedNodeIds: Set<number>
    ): IFlowChart => {
      // Step 1: Filter nodes to only include selected ones (for FlowChart -> node)
      const selectedNodes = reportData.node.filter((node) =>
        selectedNodeIds.has(node.id)
      );

      // Step 2: Filter links to include links connected to selected nodes
      // Include links where at least one endpoint (fromNodeID or toNodeID) is in selected nodes
      const selectedLinks = reportData.link.filter((link) => {
        const fromSelected = selectedNodeIds.has(link.fromNodeID);
        const toSelected = selectedNodeIds.has(link.toNodeID);
        // Include links where at least one node is selected
        return fromSelected || toSelected;
      });

      // Step 3: Map selected nodes to IFlowChartNode format
      // Only include directly selected nodes in FlowChart -> node
      // Include all fields needed for bulk popup format
      const nodes: IFlowChartNode[] = selectedNodes.map((node: INode) => ({
        id: node.id,
        name: node.name,
        sentCount: node.sentCount,
        recievedCount: node.recievedCount,
        isRemainig: node.isRemainig,
        x: node.x || 0,
        y: node.y || 0,
        radius: node.radius || 0,
        exchangeCount: node.exchangeCount || 0,
      }));

      // Step 4: Map selected links to IFlowChartLink format
      // Only include required fields: fromNodeID, toNodeID, fromCount, toCount, connectorExchangeCount
      const links: IFlowChartLink[] = selectedLinks.map(
        (link: ILink) =>
          ({
            fromNodeID: link.fromNodeID,
            toNodeID: link.toNodeID,
            fromCount: link.fromCount,
            toCount: link.toCount,
            connectorExchangeCount: link.connectorExchangeCount,
          }) as IFlowChartLink
      );

      // Step 5: Build connectors from selected links
      // Look up node names from the full reportData.node array to ensure both from and to are populated
      // even if the connected node is not directly selected
      const connectors: IConnector[] = selectedLinks.map((link: ILink) => {
        const fromNode = reportData.node.find(
          (n: INode) => n.id === link.fromNodeID
        );
        const toNode = reportData.node.find(
          (n: INode) => n.id === link.toNodeID
        );

        return {
          from: fromNode?.name || '',
          to: toNode?.name || '',
          fromNodeID: link.fromNodeID,
          toNodeID: link.toNodeID,
        };
      });

      return {
        node: nodes,
        link: links,
        exchangeDocs: [], // Not needed - passing empty array
        connector: connectors,
        communicationLiteralValue: [], // Not needed - passing empty array
      };
    },
    []
  );

  // Helper function to build communication data request (memoized with useCallback)
  // Must be defined before handleViewInDocList and handleViewInDocView
  const buildCommunicationDataRequest =
    useCallback((): ICommunicationDataRequest | null => {
      const currentReqData = reqDataRef.current;
      const currentReportCriteria = reportCriteriaRef.current;
      const currentReportData = currentReportDataRef.current;
      const selectedNodeIds = selectedNodeIdsRef.current;

      // Use reportIDFromUrl as fallback when page reloads with report ID
      const reportId = currentReqData?.reportId || reportIDFromUrl;

      if (
        !reportId ||
        reportId === 0 ||
        !currentReportCriteria ||
        !currentReportData
      ) {
        return null;
      }

      // Validate that at least one node is selected
      if (selectedNodeIds.size === 0) {
        return null;
      }

      // Build flowChart with ONLY selected nodes and their connections
      const flowChart = buildFlowChartFromSelectedNodes(
        currentReportData,
        selectedNodeIds
      );

      // Map filters from ReportPayload to ICommunicationDataFilter format
      const filters: ICommunicationDataFilter[] = (
        currentReportCriteria.filters || []
      ).map((filter) => ({
        projectFieldId: filter.projectFieldId || '',
        projectFieldName: filter.projectFieldName || '',
        dataType: filter.dataType || '',
        projectFieldLabel: filter.projectFieldLabel || '',
        type:
          typeof filter.type === 'string'
            ? Number(filter.type) || 0
            : typeof filter.type === 'number'
              ? filter.type
              : 0,
        operand: filter.operand || '',
        values: filter.values || [],
        metaDataID: filter.metaDataID || 0,
        text: filter.text || '',
      }));

      // Use fallback values from Redux state and showBy state if currentReportCriteria has null values
      const showByCountValue =
        currentReportCriteria.showByCount ??
        communicationShowByFilters?.topValue ??
        null;
      const showByValue =
        currentReportCriteria.showBy ??
        communicationShowByFilters?.categoryValue ??
        showBy ??
        null;
      const showByVolumeValue =
        currentReportCriteria.showByVolume ??
        communicationShowByFilters?.volumeValue ??
        null;

      const request: ICommunicationDataRequest = {
        sourceName: currentReportCriteria.sourceName || '',
        source: currentReportCriteria.source || [],
        filters: filters,
        showByCount: showByCountValue?.toString() || '',
        showBy: showByValue?.toString() || '',
        showByVolume: showByVolumeValue?.toString() || '',
        flowChart: flowChart,
        reportId: reportId.toString(),
        isDocumentCount: true,
      };

      return request;
    }, [
      buildFlowChartFromSelectedNodes,
      reportIDFromUrl,
      communicationShowByFilters,
      showBy,
    ]);

  // Helper function to convert camelCase keys to PascalCase recursively
  const convertCamelToPascal = useCallback((obj: any): any => {
    if (Array.isArray(obj)) {
      return obj.map(convertCamelToPascal);
    } else if (obj && typeof obj === 'object' && obj !== null) {
      return Object.fromEntries(
        Object.entries(obj).map(([key, value]) => [
          key.charAt(0).toUpperCase() + key.slice(1), // Convert first letter to uppercase
          convertCamelToPascal(value), // Recursively convert nested objects
        ])
      );
    }
    return obj; // Return primitive values as-is
  }, []);

  // Helper function to convert a single node to BulkPopup format with exact field names
  // Handles both camelCase and PascalCase input formats
  const convertNodeToBulkPopupFormat = useCallback((node: any) => {
    return {
      ID: node.id ?? node.ID ?? node.Id ?? 0,
      Name: node.name ?? node.Name ?? '',
      SentCount: node.sentCount ?? node.SentCount ?? 0,
      RecievedCount: node.recievedCount ?? node.RecievedCount ?? 0,
      IsRemainig: node.isRemainig ?? node.IsRemainig ?? false,
      x: node.x ?? node.X ?? 0.0,
      y: node.y ?? node.Y ?? 0.0,
      radius: node.radius ?? node.Radius ?? 0.0,
      ExchangeCount: node.exchangeCount ?? node.ExchangeCount ?? 0,
    };
  }, []);

  // Helper function to convert source details response with exact Node format for BulkPopup
  const convertSourceDetailsToBulkPopupFormat = useCallback(
    (obj: any): any => {
      if (Array.isArray(obj)) {
        return obj.map(convertSourceDetailsToBulkPopupFormat);
      } else if (obj && typeof obj === 'object' && obj !== null) {
        // Check if this object has a node array (could be node, Node, or nested in flowChart)
        const nodeArray =
          obj.node ||
          obj.Node ||
          obj.flowChart?.node ||
          obj.flowChart?.Node ||
          obj.FlowChart?.node ||
          obj.FlowChart?.Node;

        if (nodeArray && Array.isArray(nodeArray)) {
          // Convert Node array with exact format using reusable function
          const convertedNodes = nodeArray.map(convertNodeToBulkPopupFormat);

          // Convert rest of the object to PascalCase, excluding node arrays
          const rest = Object.fromEntries(
            Object.entries(obj).filter(
              ([key]) =>
                key !== 'node' &&
                key !== 'Node' &&
                key !== 'flowChart' &&
                key !== 'FlowChart'
            )
          );

          const convertedRest = convertCamelToPascal(rest);

          // If there was a flowChart, convert it properly
          if (obj.flowChart || obj.FlowChart) {
            const flowChart = obj.flowChart || obj.FlowChart;
            return {
              ...convertedRest,
              FlowChart: {
                ...convertCamelToPascal(
                  Object.fromEntries(
                    Object.entries(flowChart).filter(
                      ([key]) => key !== 'node' && key !== 'Node'
                    )
                  )
                ),
                Node: convertedNodes,
              },
            };
          }

          return {
            ...convertedRest,
            Node: convertedNodes,
          };
        }

        // For other objects, use regular PascalCase conversion
        return convertCamelToPascal(obj);
      }
      return obj; // Return primitive values as-is
    },
    [convertCamelToPascal, convertNodeToBulkPopupFormat]
  );

  // Helper function to convert bulk communication report request to PascalCase format
  // Excludes exchangeDocs and communicationLiteralValue from FlowChart as per API requirements
  const convertBulkReportRequestToPascalCase = useCallback(
    (request: IBulkCommunicationReportRequest): any => {
      const {flowChart, ...rest} = request;

      // Convert FlowChart, excluding exchangeDocs and communicationLiteralValue
      // Only include properties shown in API example
      const flowChartPascal = flowChart
        ? {
            Node: flowChart.node?.map(convertNodeToBulkPopupFormat) || [],
            Link:
              flowChart.link?.map((link) => {
                // Explicitly exclude volume - only include required fields
                const {volume, ...linkWithoutVolume} = link;
                return {
                  FromNodeID: linkWithoutVolume.fromNodeID,
                  ToNodeID: linkWithoutVolume.toNodeID,
                  FromCount: linkWithoutVolume.fromCount,
                  ToCount: linkWithoutVolume.toCount,
                  ConnectorExchangeCount:
                    linkWithoutVolume.connectorExchangeCount,
                };
              }) || [],
            Connector:
              flowChart.connector?.map((connector) => ({
                From: connector.from,
                To: connector.to,
                FromNodeID: connector.fromNodeID,
                ToNodeID: connector.toNodeID,
              })) || [],
          }
        : undefined;

      // Convert rest of the request to PascalCase
      // Only include fields shown in API example - no extra fields
      return {
        SourceName: rest.sourceName,
        Source: rest.source,
        ShowByCount: rest.showByCount,
        ShowBy: rest.showBy,
        ShowByVolume: rest.showByVolume,
        Filters: rest.filters || [],
        FlowChart: flowChartPascal,
        ReportID: rest.reportId,
        isDocumentCount: rest.isDocumentCount, // Keep camelCase as shown in example
        totalDocumentToView:
          rest.totalDocumentToView === -1 ? 0 : rest.totalDocumentToView,
        prepareList:
          !rest.isDocumentCount &&
          rest.navigateTo === SourceTypeCode.DOCLIST &&
          rest.totalDocumentToView !== -1
            ? true
            : false,
      };
    },
    [convertNodeToBulkPopupFormat]
  );

  // Helper function to build source details by calling postCommunicationData API (memoized)
  // Must be defined before onActionSelect
  const buildCommunicationSourceDetails =
    useCallback(async (): Promise<string> => {
      const request = buildCommunicationDataRequest();

      if (!request) {
        return '';
      }

      // Call postCommunicationData API
      return new Promise((resolve, reject) => {
        postCommunicationData(request, {
          onSuccess: (response) => {
            // Convert to BulkPopup format with exact Node format
            const bulkPopupFormatResponse =
              convertSourceDetailsToBulkPopupFormat(response);
            // Convert response to JSON string for sourceDetails
            const sourceDetailsString = JSON.stringify(bulkPopupFormatResponse);
            resolve(sourceDetailsString);
          },
          onError: () => {
            reject('');
          },
        });
      });
    }, [
      buildCommunicationDataRequest,
      postCommunicationData,
      convertSourceDetailsToBulkPopupFormat,
    ]);

  // Handler for ViewInDocList action (memoized)
  // Now accepts totalDocumentToView parameter (may be capped by warning modal)
  const handleViewInDocList = useCallback(
    async (totalDocumentToView?: number) => {
      // Get base request from ref (stored before showing warning modal)
      const baseRequest = baseRequestForNavigationRef.current;

      if (!baseRequest) {
        setDisplayToast(true);
        return;
      }

      // Set loading state
      setIsNavigatingToDocList(true);
      setShowDocLimitWarning(false);

      // API CALL - Prepare List with totalDocumentToView (may be capped by limit)
      const prepareListRequest: IBulkCommunicationReportRequest = {
        ...baseRequest,
        isDocumentCount: false, // Prepare list, not count
        actionFrom: SourceTypeCode.COMMUNICATIONMAP,
        navigateTo: SourceTypeCode.DOCLIST,
        prepareList: true, // Prepare the list
        totalDocumentToView: totalDocumentToView ?? 0, // Use count from warning modal (may be capped)
      };

      bulkCommunicationReport(
        convertBulkReportRequestToPascalCase(prepareListRequest),
        {
          onSuccess: (bulkResponse) => {
            // Get docListId from response
            const docListId = Number(bulkResponse.docListId);

            if (!docListId) {
              setDisplayToast(true);
              setIsNavigatingToDocList(false);
              return;
            }

            // Store in Redux
            clearDocViewState();
            dispatch(setDocListId(docListId));
            dispatch(storeDocumentListMasterId(String(docListId)));
            dispatch(storeSourceTypeCode(SourceTypeCode.COMMUNICATIONMAP));

            // Store back navigation info
            const backPath = `${window.location.pathname}${window.location.search}`;
            dispatch(
              setDocListBackNavigation({
                label: t('communicationExplorer.header.title'),
                path: backPath,
              })
            );

            // Call ActionMenuWrapper's callback to update its internal state
            if (navigationCallbackRef.current) {
              navigationCallbackRef.current(
                String(docListId),
                SourceTypeCode.COMMUNICATIONMAP
              );
              navigationCallbackRef.current = undefined;
            }

            // Navigate immediately after storing in Redux
            setIsNavigatingToDocList(false);
            setTimeout(() => {
              navigate('/Document/DocList');
            }, 0);
          },
          onError: () => {
            setDisplayToast(true);
            setIsNavigatingToDocList(false);
          },
        }
      );
    },
    [
      bulkCommunicationReport,
      convertBulkReportRequestToPascalCase,
      dispatch,
      navigate,
      t,
    ]
  );

  // Handler for ViewInDocView action (memoized)
  // Now accepts totalDocumentToView parameter (may be capped by warning modal)
  const handleViewInDocView = useCallback(
    async (totalDocumentToView?: number) => {
      // Get base request from ref (stored before showing warning modal)
      const baseRequest = baseRequestForNavigationRef.current;

      if (!baseRequest) {
        setDisplayToast(true);
        return;
      }

      // Set loading state
      setIsNavigatingToDocView(true);
      setShowDocLimitWarning(false);

      // API CALL - Prepare List with totalDocumentToView (may be capped by limit)
      const prepareListRequest: IBulkCommunicationReportRequest = {
        ...baseRequest,
        isDocumentCount: false, // Prepare list, not count
        actionFrom: SourceTypeCode.COMMUNICATIONMAP,
        navigateTo: SourceTypeCode.DOCVIEW, // Different from DocList
        prepareList: true, // Prepare the list
        totalDocumentToView: totalDocumentToView ?? 0, // Use count from warning modal (may be capped)
      };

      bulkCommunicationReport(prepareListRequest, {
        onSuccess: (bulkResponse) => {
          // Get docListId from response
          const docListId = Number(bulkResponse.docListId);

          if (!docListId) {
            setDisplayToast(true);
            setIsNavigatingToDocView(false);
            return;
          }

          // Store in Redux
          clearDocViewState();
          dispatch(storeDocumentListMasterId(String(docListId)));
          dispatch(storeSourceTypeCode(SourceTypeCode.COMMUNICATIONMAP));

          // Call ActionMenuWrapper's callback to update its internal state
          if (navigationCallbackRef.current) {
            navigationCallbackRef.current(
              String(docListId),
              SourceTypeCode.COMMUNICATIONMAP
            );
            navigationCallbackRef.current = undefined;
          }

          // Navigate immediately after storing in Redux
          setIsNavigatingToDocView(false);
          setTimeout(() => {
            navigate('/DocumentViewer/DocView');
          }, 0);
        },
        onError: () => {
          setDisplayToast(true);
          setIsNavigatingToDocView(false);
        },
      });
    },
    [
      bulkCommunicationReport,
      convertBulkReportRequestToPascalCase,
      dispatch,
      navigate,
    ]
  );

  // Handler for ViewInDocView in new tab (memoized)
  // Now accepts totalDocumentToView parameter (may be capped by warning modal)
  const handleViewInDocViewNewTab = useCallback(
    async (totalDocumentToView?: number) => {
      // Get base request from ref (stored before showing warning modal)
      const baseRequest = baseRequestForNavigationRef.current;

      if (!baseRequest) {
        setDisplayToast(true);
        return;
      }

      // Set loading state
      setIsNavigatingToDocView(true);
      setShowDocLimitWarning(false);

      // API CALL - Prepare List with totalDocumentToView (may be capped by limit)
      const prepareListRequest: IBulkCommunicationReportRequest = {
        ...baseRequest,
        isDocumentCount: false, // Prepare list, not count
        actionFrom: SourceTypeCode.COMMUNICATIONMAP,
        navigateTo: SourceTypeCode.DOCVIEW, // Same as regular DocView
        prepareList: true, // Prepare the list
        totalDocumentToView: totalDocumentToView ?? 0, // Use count from warning modal (may be capped)
      };

      bulkCommunicationReport(prepareListRequest, {
        onSuccess: (bulkResponse) => {
          // Get docListId from response
          const docListId = Number(bulkResponse.docListId);

          if (!docListId) {
            setDisplayToast(true);
            setIsNavigatingToDocView(false);
            return;
          }

          // Store in Redux for the new tab to access
          clearDocViewState();
          dispatch(storeDocumentListMasterId(String(docListId)));
          dispatch(storeSourceTypeCode(SourceTypeCode.COMMUNICATIONMAP));

          // Call ActionMenuWrapper's callback to update its internal state
          if (navigationCallbackRef.current) {
            navigationCallbackRef.current(
              String(docListId),
              SourceTypeCode.COMMUNICATIONMAP
            );
            navigationCallbackRef.current = undefined;
          }

          // Open in new tab
          setIsNavigatingToDocView(false);
          setTimeout(() => {
            const newTab = window.open('/DocumentViewer/DocView', '_blank');
            if (!newTab) {
              setDisplayToast(true);
            }
          }, 0);
        },
        onError: () => {
          setDisplayToast(true);
          setIsNavigatingToDocView(false);
        },
      });
    },
    [bulkCommunicationReport, dispatch]
  );

  // Function to get document count and show warning modal
  const getDocumentCountAndShowWarning = useCallback(
    async (actionId: string) => {
      // Validate required data - use reportIDFromUrl as fallback when page reloads with report ID
      const currentReqData = reqDataRef.current;
      const currentSelectedNodeIds = selectedNodeIdsRef.current;
      const reportId = currentReqData?.reportId || reportIDFromUrl;

      if (!reportId || reportId === 0) {
        setDisplayToast(true);
        return;
      }

      // Validate that at least one node is selected
      if (currentSelectedNodeIds.size === 0) {
        setDisplayToast(true);
        return;
      }

      // Validate that report data is loaded
      if (!currentReportDataRef.current) {
        setDisplayToast(true);
        return;
      }

      // Validate that report criteria is available
      if (!reportCriteriaRef.current) {
        setDisplayToast(true);
        return;
      }

      // Build base communication data request
      const baseRequest = buildCommunicationDataRequest();
      if (!baseRequest) {
        setDisplayToast(true);
        return;
      }

      // Store base request in ref for later use in handlers
      baseRequestForNavigationRef.current = baseRequest;

      // Set navigation type and action ID for warning modal
      if (actionId === 'view_doc_list') {
        setNavigationType(SourceTypeCode.DOCLIST);
      } else if (
        actionId === 'view_doc_view' ||
        actionId === 'view_doc_view_new_tab'
      ) {
        setNavigationType(SourceTypeCode.DOCVIEW);
      }
      setSelectedActionId(actionId);

      // API CALL - Get Document Count
      const countRequest: IBulkCommunicationReportRequest = {
        ...baseRequest,
        isDocumentCount: true, // Get document count
        actionFrom: SourceTypeCode.COMMUNICATIONMAP,
        navigateTo:
          actionId === 'view_doc_list'
            ? SourceTypeCode.DOCLIST
            : SourceTypeCode.DOCVIEW,
        prepareList: false, // Don't prepare list yet
        totalDocumentToView: 0, // Not used in count call
      };

      bulkCommunicationReport(
        convertBulkReportRequestToPascalCase(countRequest),
        {
          onSuccess: (countResponse) => {
            // Extract document count
            const documentCount = countResponse.documentCount || 0;

            if (documentCount === 0) {
              setDisplayToast(true);
              return;
            }

            // Store count and show warning modal
            setDocumentCountFromAPI(documentCount);
            setShowDocLimitWarning(true);
          },
          onError: () => {
            setDisplayToast(true);
          },
        }
      );
    },
    [
      buildCommunicationDataRequest,
      bulkCommunicationReport,
      convertBulkReportRequestToPascalCase,
      reportIDFromUrl,
    ]
  );

  // Helper function to start document navigation with potentially capped document count
  const startDocumentNavigation = useCallback(
    (actionId: string, totalDocumentToView: number) => {
      if (actionId === 'view_doc_list') {
        handleViewInDocList(totalDocumentToView);
      } else if (actionId === 'view_doc_view') {
        handleViewInDocView(totalDocumentToView);
      } else if (actionId === 'view_doc_view_new_tab') {
        handleViewInDocViewNewTab(totalDocumentToView);
      }
    },
    [handleViewInDocList, handleViewInDocView, handleViewInDocViewNewTab]
  );

  // Prepare action menu items for ActionMenuWrapper
  const actionMenuItems = useMemo((): CustomMenuItem[] => {
    const commonItems: CustomMenuItem[] = [
      {
        id: ReportActionMenuOptionTypes.View,
        label: t('reports.communicationExplorer.actionMenuItems.view'),
        submenu: [
          {
            id: ReportActionMenuOptionTypes.ViewInDocView,
            label: t(
              'reports.communicationExplorer.actionMenuItems.viewInDocView'
            ),
          },
          {
            id: ReportActionMenuOptionTypes.ViewInDocViewNewTab,
            label: t(
              'reports.communicationExplorer.actionMenuItems.viewInDocViewInNewTab'
            ),
          },
          {
            id: ReportActionMenuOptionTypes.ViewInDocList,
            label: t(
              'reports.communicationExplorer.actionMenuItems.viewInDocList'
            ),
          },
        ],
      },
      {
        id: ReportActionMenuOptionTypes.BulkTag,
        label: t('reports.communicationExplorer.actionMenuItems.bulkTag'),
      },
      {
        id: ReportActionMenuOptionTypes.BulkFolder,
        label: t('reports.communicationExplorer.actionMenuItems.bulkFolder'),
      },
    ];

    const roleSpecificItem: CustomMenuItem | null =
      roleId?.toString() === RoleId.ReviewManager
        ? {
            id: ReportActionMenuOptionTypes.BulkAssign,
            label: t(
              'reports.communicationExplorer.actionMenuItems.bulkAssign'
            ),
          }
        : roleId?.toString() === RoleId.ProjectAdministrator
          ? {
              id: ReportActionMenuOptionTypes.BulkRelease,
              label: t(
                'reports.communicationExplorer.actionMenuItems.bulkRelease'
              ),
            }
          : null;

    // Add custom actions (tally, timeline_and_gaps, concept_explorer) that are not handled by ActionMenuWrapper
    return [
      ...(roleSpecificItem ? [...commonItems, roleSpecificItem] : commonItems),
      {
        id: ReportActionMenuOptionTypes.Tally,
        label: t('reports.communicationExplorer.actionMenuItems.tally'),
      },
      {
        id: 'concept_explorer',
        label: t(
          'reports.communicationExplorer.actionMenuItems.conceptExplorer'
        ),
      },
      {
        id: ReportActionMenuOptionTypes.TimelineAndGaps,
        label: t(
          'reports.communicationExplorer.actionMenuItems.timelineAndGaps'
        ),
      },
    ];
  }, [t, roleId]);

  // Get source details for ActionMenuWrapper (synchronous version for props)
  const getSourceDetailsForActionMenu = useMemo(() => {
    // This will be built on demand - ActionMenuWrapper will call getSourceDetails
    return {};
  }, []);

  // Customize doAction payload for CommunicationExplorer
  const getDoActionData = useCallback(
    (data: {operationType?: string; sourceDetails?: {[key: string]: any}}) => {
      // Return sourceDetails as selectedList
      return {
        selectedList: data.sourceDetails || {},
        destinationList: {},
      };
    },
    []
  );

  // Build sourceDetails on-demand when bulk action is selected
  const buildSourceDetails = useCallback(async (): Promise<{
    [key: string]: any;
  }> => {
    // Build sourceDetails by calling postCommunicationData API and converting to PascalCase
    const sourceDetailsString = await buildCommunicationSourceDetails();
    if (!sourceDetailsString || sourceDetailsString === '') {
      return {};
    }
    try {
      const parsed = JSON.parse(sourceDetailsString);
      return parsed;
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (error) {
      return {};
    }
  }, [buildCommunicationSourceDetails]);

  // Show toast from ActionMenuWrapper
  const showToastFromParent = useCallback(() => {
    setDisplayToast(true);
  }, []);

  // Store callback from ActionMenuWrapper for navigation actions
  const navigationCallbackRef = useRef<
    ((docId: string, source: string) => void) | undefined
  >(undefined);

  // Store callback from ActionMenuWrapper for tally action
  const tallyCallbackRef = useRef<
    ((docId: string, source: string, ...args: any[]) => void) | undefined
  >(undefined);

  // Ref to store handleMenuAction to avoid circular dependency
  const handleMenuActionRef = useRef<((actionId: string) => void) | undefined>(
    undefined
  );

  const handleConceptExplorerNavigation = useCallback(() => {
    // Navigate to Concept Explorer directly
    navigate('/DataAnalysisReport/ConceptExplorer');
  }, [navigate]);

  const handleConceptExploreAction = () => {
    const initPayload = buildCommunicationDataRequest();
    const payload = {
      ...initPayload,
      sourceTypeCode: 'COMMUNICATIONMAP',
      actionFrom: 'COMMUNICATIONMAP',
      navigateTo: 'CONCEPT',
      previousUrl: '/Report/RedirectToReports/CommunicationExplorerRepor',
      prepareList: true,
      totalDocumentToView: -1,
    };
    communicationBulkReport(payload, {
      onSuccess: (data) => {
        const backPath = `${window.location.pathname}${window.location.search}`;
        const communicationState = {
          reportId: Number(reqData?.reportId || reportIDFromUrl),
          backSourceLabel: 'Communication Explorer',
          sourceId: data.docListID?.toString() ?? '',
          backSourcePath: backPath,
          communicationStateValue: {
            selectedSourceMap,
            selectedNodeIds,
            selectedLinkIds,
            currentReportData,
          },
          sourceName: 'DOCLIST',
          navigationSourceType: 'documents',
          sourceSelectionText: t(
            'reports.communicationExplorer.fromCommunicationExplorer'
          ),
        };
        dispatch(setReportDetails(communicationState));
        handleConceptExplorerNavigation();
      },
      onError: () => {},
    });
  };

  const [initialized, setInitialized] = useState(false);

  useEffect(() => {
    if (!communicationSourceId || !communicationStateValue) return;
    const currentPath = `${window.location.pathname}${window.location.search}`;
    if (
      communicationSourceId &&
      currentPath.includes('CommunicationExplorer')
    ) {
      setSelectedSourceMap(communicationStateValue.selectedSourceMap || {});
      setSelectedNodeIds(communicationStateValue?.selectedNodeIds);
      setSelectedLinkIds(communicationStateValue?.selectedLinkIds);
      setCurrentReportData(
        communicationStateValue?.currentReportData || {
          node: [],
          link: [],
          exchangeDocs: [],
        }
      );
      dispatch(clearReportBackNavigation());
      setInitialized(true);
    }
  }, [
    backSourcePath,
    communicationSourceId,
    Object.keys(selectedSourceMap).length,
    initialized,
    dispatch,
    communicationStateValue,
  ]);

  // Handle action pick from ActionMenuWrapper
  // Note: handleMenuAction is defined later, so we'll use a ref to avoid circular dependency
  const handleActionPick = useCallback(
    (
      action: CustomMenuItem,
      callBackForDoc?: (docId: string, source: string) => void
    ) => {
      const actionId = action.id as ReportActionMenuOptionTypes | string;

      // Handle custom action (concept_explorer) that doesn't need validation
      if (action.id === 'concept_explorer') {
        // Handle concept_explorer navigation - this is a custom action
        // Could call onActionSelect if needed, but for now just return
        handleConceptExploreAction();
        return;
      }

      // Handle tally action
      if (
        actionId === ReportActionMenuOptionTypes.Tally ||
        actionId === 'tally'
      ) {
        // Store the callback to call after API success
        tallyCallbackRef.current = callBackForDoc;
        // Call handleMenuActionRef which will be set later
        if (handleMenuActionRef.current) {
          handleMenuActionRef.current('tally');
        }
        return;
      }

      // Handle timeline and gaps action
      if (
        actionId === ReportActionMenuOptionTypes.TimelineAndGaps ||
        actionId === 'timeline_and_gaps'
      ) {
        // Store the callback to call after API success
        tallyCallbackRef.current = callBackForDoc;
        // Call handleMenuActionRef which will be set later
        if (handleMenuActionRef.current) {
          handleMenuActionRef.current('timeline_and_gaps');
        }
        return;
      }

      // Handle navigation actions
      if (
        actionId === ReportActionMenuOptionTypes.ViewInDocList ||
        actionId === ReportActionMenuOptionTypes.ViewInDocView ||
        actionId === ReportActionMenuOptionTypes.ViewInDocViewNewTab
      ) {
        // Store the callback to call after API success
        navigationCallbackRef.current = callBackForDoc;

        // Map action IDs to internal format
        let internalActionId = '';
        if (actionId === ReportActionMenuOptionTypes.ViewInDocList) {
          internalActionId = 'view_doc_list';
        } else if (actionId === ReportActionMenuOptionTypes.ViewInDocView) {
          internalActionId = 'view_doc_view';
        } else if (
          actionId === ReportActionMenuOptionTypes.ViewInDocViewNewTab
        ) {
          internalActionId = 'view_doc_view_new_tab';
        }

        // Get document count and show warning modal
        if (internalActionId) {
          getDocumentCountAndShowWarning(internalActionId);
        }
      }
    },
    [
      getDocumentCountAndShowWarning,
      selectedLinkIds,
      selectedSourceMap,
      selectedNodeIds,
    ]
  );

  // Validate before allowing bulk actions
  const validateBeforeBulkAction = useCallback((): boolean => {
    const currentReqData = reqDataRef.current;
    const currentSelectedNodeIds = selectedNodeIdsRef.current;

    if (!currentReqData || !currentReqData.reportId) {
      setDisplayToast(true);
      return false;
    }

    if (currentSelectedNodeIds.size === 0) {
      setDisplayToast(true);
      return false;
    }

    return true;
  }, []);

  // Check if menu should be disabled
  const isMenuDisabled = useMemo(() => {
    return selectedNodeIds.size === 0;
  }, [selectedNodeIds]);

  // Handle bulk tag continue
  const handleBulkTagContinue = async (selectedAction: string) => {
    setBulkSelectedAction(selectedAction);

    const bulkActionTreeIDs = (() => {
      const ids = selectedNewExistingTag?.ids;
      if (Array.isArray(ids)) return ids;
      if (typeof ids === 'string') return [ids];
      return [];
    })();

    if (selectedAction === 'untagDocs') {
      setIsBulkWarning(true);
    } else if (selectedAction === 'tagDocs') {
      callBulkActionsLikePopupApiCall(
        'TAG',
        bulkActionTreeIDs,
        selectedAction,
        undefined,
        () => {
          setIsActionLikeModal('bulkTag');
        }
      );
    }
  };

  // Handle bulk folder continue
  const handleBulkFolderContinue = async (
    action: string,
    docCount?: number,
    newFolderId?: number
  ) => {
    setBulkSelectedAction(action);

    const bulkActionTreeIDs = (() => {
      if (action === 'folderDocs' && newFolderId) {
        return [newFolderId.toString()];
      }
      const ids = selectedNewExistingFolder?.ids;
      if (Array.isArray(ids)) return ids;
      if (typeof ids === 'string') return [ids];
      return [];
    })();

    if (action === 'folderDocs') {
      // If action is folderDocs, then we need to execute like popup call
      callBulkActionsLikePopupApiCall(
        'FOLDER',
        bulkActionTreeIDs,
        action,
        masterDocListId,
        () => {
          setIsActionLikeModal('bulkFolder');
        }
      );
    } else if (action === 'unfolderDocs') {
      // If action is unfolderDocs, then we need to execute doAction API call
      doActionApiCall('FOLDER', bulkActionTreeIDs, action, undefined, docCount);
      setIsBulkFolderAction(false);
    }
  };

  // Handle bulk release
  const handleBulkRelease = (selectedIds: string[]) => {
    setBulkSelectedAction('bulkRelease');
    setSecurityGroupId(selectedIds);
    callBulkActionsLikePopupApiCall(
      'RELEASE',
      selectedIds,
      'bulkRelease',
      masterDocListId,
      () => {
        setIsActionLikeModal('bulkRelease');
      }
    );
  };

  // Handle bulk unrelease
  const handleBulkUnrelease = (selectedIds: string[]) => {
    doActionApiCall('RELEASE', selectedIds, 'bulkUnrelease');
    setIsBulkReleaseAction(false);
  };

  // Handle bulk assign continue
  const handleBulkAssignContinue = async (payload: {
    action: 'assign_documents' | 'unassign_documents';
    isNewAssignment: boolean;
    samplingMethod?: string;
    persistContent?: boolean;
    count?: number;
    percent?: number;
  }) => {
    const {
      action,
      isNewAssignment,
      samplingMethod = 'count',
      persistContent = false,
      count = 1,
      percent = 1,
    } = payload;

    setBulkSelectedAction(action);
    const isAdd = action !== 'unassign_documents';
    const isNew = isAdd ? isNewAssignment : false;

    if (action === 'assign_documents') {
      const mapSamplingMethodToApi = (method: string): string => {
        const mapping: Record<string, string> = {
          count: 'STANDARD',
          percent: 'PERCENT',
          parent_level_docs_only: 'PARENT_LEVEL_DOCS_ONLY',
          inclusive_email: 'INCLUSIVE_EMAIL',
        };
        return mapping[method] || 'STANDARD';
      };

      const mappedSamplingMethod = mapSamplingMethodToApi(samplingMethod);

      const mapNumberToAssign =
        samplingMethod === 'count'
          ? count.toString()
          : samplingMethod === 'percent'
            ? percent !== undefined
              ? percent.toString()
              : ''
            : '0';

      samplingMethodForAssign.current = samplingMethod;
      countForAssign.current = count;
      percentForAssign.current = percent;

      // Build source details object for ContextContainer
      buildCommunicationSourceDetails()
        .then((sourceDetailsString) => {
          const sourceDetailsObject = JSON.parse(sourceDetailsString);

          const bulkAssignDocIdPayload: IBulkAssignDocListIdRequest = {
            ActionType: 'ASSIGNMENT',
            ElementID: 'divBulkAction',
            BulkActionContextTypeCode: SourceTypeCode.COMMUNICATIONMAP as any,
            DocumentCount: '0',
            ContextContainer: sourceDetailsObject || {},
            UniqueDocumentCount: 0,
            DestinationContextTypeCode: 'BULKASSIGN',
            SampleMethod: mappedSamplingMethod,
            SourceContextTypeCode: 'ASSIGNDOCNOC',
            BulkActionTreeIDs: selectedTreeIds,
            IsAdd: isAdd,
            IsNewAssignment: isNew,
            IsPersistSearchHits: persistContent,
            BulkActionTypeCode: 'ASSIGN',
            SourceContextID: masterDocListId.toString(),
            NumberToAssign: mapNumberToAssign,
          };

          persistContentForAssign.current = persistContent;
          pendingAssignAction.current = action;
          if (isNew) assignmentSelectedTab.current = 'new';
          getBulkAssignDocListId(bulkAssignDocIdPayload);
        })
        .catch(() => {
          setDisplayToast(true);
        });
    } else {
      pendingAssignAction.current = action;
      doActionApiCall('ASSIGN', selectedTreeIds, 'unassign_documents');
    }
  };

  // Call bulk actions like popup API
  const callBulkActionsLikePopupApiCall = (
    getActionType: string,
    bulkActionTreeIDs: string[],
    selectedAction?: string,
    masterDocListIdToUse?: number,
    onSuccessCallback?: () => void,
    persistContent?: boolean
  ) => {
    const bulkTreeIDs = bulkActionTreeIDs.map(Number);
    const masterDocListIdForPayload = masterDocListIdToUse ?? masterDocListId;

    // Need to get sourceDetails again - call API
    buildCommunicationSourceDetails()
      .then((sourceDetails) => {
        const likePopupPayload = {
          sourceType: SourceTypeCode.COMMUNICATIONMAP as any,
          actionType: getActionType,
          elementID: 'divBulkAction',
          sourceTypeContext: 'COMMNOC',
          sourceDetails: sourceDetails,
          destinationTypeContext: '',
          selectedHitsDetails: [],
          documentCount: '0',
          masterDocListID: masterDocListIdForPayload,
          isAdd: true,
          documentListMasterID: 0,
          docAssign: 0,
          selectedDetinationIds: JSON.stringify({
            BulkActionTreeIDs: bulkTreeIDs,
          }),
          ...(getActionType === 'ASSIGN' && {
            isPersistSearchHits:
              persistContent !== undefined
                ? persistContent
                : persistContentForAssign.current,
          }),
        };

        callBulkActionsLikePopup(likePopupPayload, {
          onSuccess: () => {
            if (onSuccessCallback) {
              onSuccessCallback();
            }
          },
        });
      })
      .catch(() => {
        setDisplayToast(true);
      });
  };

  // Handle action like (related documents)
  const handleActionLike = (docCount?: number) => {
    if (isActionLikeModal === 'bulkTag') {
      setIsBulkWarning(true);
    }
    if (isActionLikeModal === 'bulkRelease') {
      setIsBulkReleaseAction(false);
      doActionApiCall('RELEASE', securityGroupId, bulkSelectedAction);
    }
    if (isActionLikeModal === 'bulkAssign') {
      setIsBulkAssignAction(false);
      doActionApiCall(
        'ASSIGN',
        selectedTreeIds,
        bulkSelectedAction,
        undefined,
        docCount
      );
    }
    if (isActionLikeModal === 'bulkFolder') {
      setIsBulkFolderAction(false);
      const bulkActionTreeIDs = (() => {
        const ids = selectedNewExistingFolder?.ids;
        if (Array.isArray(ids)) return ids;
        if (typeof ids === 'string') return [ids];
        return [];
      })();
      doActionApiCall(
        'FOLDER',
        bulkActionTreeIDs,
        bulkSelectedAction,
        undefined,
        docCount
      );
    }
    setIsActionLikeModal(false);
  };

  // Handle bulk warning continue
  const handleBulkWarning = () => {
    setActionLikeChangeList(undefined);
    const bulkActionTreeIDs = (() => {
      const ids = selectedNewExistingTag?.ids;
      if (Array.isArray(ids)) return ids;
      if (typeof ids === 'string') return [ids];
      return [];
    })();

    doActionApiCall('TAG', bulkActionTreeIDs, bulkSelectedAction);
  };

  // Do action API call
  const doActionApiCall = (
    getActionType: string,
    bulkActionTreeIDs: string[],
    selectedAction: string,
    assignmentId?: number,
    docCount?: number
  ) => {
    const IsAdd =
      selectedAction === 'tagDocs' ||
      selectedAction === 'assign_documents' ||
      selectedAction === 'bulkRelease' ||
      selectedAction === 'folderDocs';

    let sampleMethod = 'STANDARD';
    let numberToAssignValue = '0';

    if (selectedAction === 'assign_documents') {
      const samplingMethod = samplingMethodForAssign.current;
      switch (samplingMethod) {
        case 'count':
          sampleMethod = 'STANDARD';
          numberToAssignValue = countForAssign.current?.toString() ?? '0';
          break;

        case 'percent': {
          sampleMethod = 'PERCENTAGE';
          const calculatedCount = Math.round(
            ((docCount ?? 0) * (percentForAssign.current ?? 0)) / 100
          );
          numberToAssignValue = calculatedCount.toString();
          break;
        }

        case 'parent_level_docs_only':
          sampleMethod = 'PARENTLEVEL';
          numberToAssignValue = '0';
          break;

        case 'inclusive_email':
          sampleMethod = 'INCLUSIVEEMAIL';
          numberToAssignValue = '0';
          break;

        default:
          sampleMethod = 'STANDARD';
          numberToAssignValue = '0';
          break;
      }
    } else {
      sampleMethod = 'STANDARD';
      numberToAssignValue = docCount !== undefined ? docCount.toString() : '0';
    }

    const includeFlags = {
      IncludeNearDuplicates:
        actionLikeChangeList?.find((item) => item.id === 'near_duplicates')
          ?.selected === true,
      IncludeFamilyMember:
        actionLikeChangeList?.find((item) => item.id === 'family_members')
          ?.selected === true,
      IncludeEmailThreads:
        actionLikeChangeList?.find((item) => item.id === 'email_docs')
          ?.selected === true,
    };

    // Build source details as object for selectedList
    buildCommunicationSourceDetails()
      .then((sourceDetailsString) => {
        const sourceDetailsObject = JSON.parse(sourceDetailsString);

        const doActionPayload = {
          sourceType: SourceTypeCode.COMMUNICATIONMAP as any,
          actionType: getActionType,
          elementId: 'divBulkAction',
          selectedList: {
            ...(sourceDetailsObject || {}),
          },
          destinationList: {
            BulkActionTreeIDs: bulkActionTreeIDs,
            IsAdd: IsAdd,
            SampleMethod: sampleMethod,
            NumberToAssign: numberToAssignValue,
            MasterDocListID: masterDocListId.toString(),
            ...(IsAdd === true && {
              IncludeNearDuplicates: includeFlags.IncludeNearDuplicates,
              IncludeFamilyMember: includeFlags.IncludeFamilyMember,
              IncludeEmailThreads: includeFlags.IncludeEmailThreads,
            }),
          },
          documentListMasterID: masterDocListId,
          strSelections: null,
        };

        doAction(doActionPayload);
      })
      .catch(() => {
        setDisplayToast(true);
      });
  };

  // Action like change handler
  const onActionLikeChange = (
    index: number,
    status: boolean,
    updatedActionItems: IActionListItem[]
  ) => {
    setActionLikeChangeList(updatedActionItems);
  };

  // Get selected new existing tag IDs
  const getSelectedNewExistingTag = (): string[] => {
    const ids = selectedNewExistingTag?.ids;
    if (!ids) return [];
    if (typeof ids === 'string') return ids ? [ids] : [];
    return ids || [];
  };

  const handleMenuAction = useCallback(
    (actionId: string) => {
      if (actionId === 'tally') {
        // Validate required data - use reportIDFromUrl as fallback when page reloads with report ID
        const currentReqData = reqDataRef.current;
        const currentSelectedNodeIds = selectedNodeIdsRef.current;
        const reportId = currentReqData?.reportId || reportIDFromUrl;

        if (!reportId || reportId === 0) {
          setDisplayToast(true);
          return;
        }

        // Validate that at least one node is selected
        if (currentSelectedNodeIds.size === 0) {
          setDisplayToast(true);
          return;
        }

        // Validate that report data is loaded
        if (!currentReportDataRef.current) {
          setDisplayToast(true);
          return;
        }

        // Validate that report criteria is available
        if (!reportCriteriaRef.current) {
          setDisplayToast(true);
          return;
        }

        // Build base communication data request
        const baseRequest = buildCommunicationDataRequest();
        if (!baseRequest) {
          setDisplayToast(true);
          return;
        }

        // Single API call to get docListId
        const tallyRequest: IBulkCommunicationReportRequest = {
          ...baseRequest,
          isDocumentCount: false, // Get docListId, not count
          actionFrom: SourceTypeCode.COMMUNICATIONMAP,
          navigateTo: SourceTypeCode.DOCLIST,
          prepareList: true, // Prepare the list
          totalDocumentToView: -1, // Use -1 as specified
        };

        bulkCommunicationReport(
          convertBulkReportRequestToPascalCase(tallyRequest),
          {
            onSuccess: (bulkResponse) => {
              // Get docListId from response
              const docListId = Number(bulkResponse.docListId);

              if (!docListId) {
                setDisplayToast(true);
                return;
              }

              // Call ActionMenuWrapper's callback for tally navigation
              if (tallyCallbackRef.current) {
                // Cast to call with 3 parameters - runtime supports it
                (tallyCallbackRef.current as any)(
                  String(docListId),
                  SourceTypeCode.DOCLIST,
                  SourceTypeCode.COMMUNICATIONMAP
                );
                tallyCallbackRef.current = undefined;
              } else {
                // Fallback: navigate directly if callback is not available
                navigate('/Report/Tally', {
                  state: {
                    navigationSourceType: SourceTypeCode.DOCLIST,
                    source: [String(docListId)],
                    sourceName: SourceTypeCode.COMMUNICATIONMAP,
                  },
                });
              }
            },
            onError: () => {
              setDisplayToast(true);
            },
          }
        );
      } else if (actionId === 'timeline_and_gaps') {
        // Validate required data - use reportIDFromUrl as fallback when page reloads with report ID
        const currentReqData = reqDataRef.current;
        const currentSelectedNodeIds = selectedNodeIdsRef.current;
        const reportId = currentReqData?.reportId || reportIDFromUrl;

        if (!reportId || reportId === 0) {
          setDisplayToast(true);
          return;
        }

        // Validate that at least one node is selected
        if (currentSelectedNodeIds.size === 0) {
          setDisplayToast(true);
          return;
        }

        // Validate that report data is loaded
        if (!currentReportDataRef.current) {
          setDisplayToast(true);
          return;
        }

        // Validate that report criteria is available
        if (!reportCriteriaRef.current) {
          setDisplayToast(true);
          return;
        }

        // Build base communication data request
        const baseRequest = buildCommunicationDataRequest();
        if (!baseRequest) {
          setDisplayToast(true);
          return;
        }

        // Single API call to get docListId
        const timelineRequest: IBulkCommunicationReportRequest = {
          ...baseRequest,
          isDocumentCount: false, // Get docListId, not count
          actionFrom: SourceTypeCode.COMMUNICATIONMAP,
          navigateTo: SourceTypeCode.DOCLIST,
          prepareList: true, // Prepare the list
          totalDocumentToView: -1, // Use -1 as specified
        };

        bulkCommunicationReport(
          convertBulkReportRequestToPascalCase(timelineRequest),
          {
            onSuccess: (bulkResponse) => {
              // Get docListId from response
              const docListId = Number(bulkResponse.docListId);

              if (!docListId) {
                setDisplayToast(true);
                return;
              }

              // Dispatch timeline ingestion data and navigate
              dispatch(
                setTimelineIngestion({
                  sourceName: 'Documents',
                  sourceType: SourceTypeCode.DOCLIST,
                  source: {
                    id: String(docListId),
                    name: 'Selected Documents from Communication Map',
                  },
                  navigationSourceType: SourceTypeCode.COMMUNICATIONMAP,
                })
              );
              setTimeout(() => {
                navigate('/Report/Timeline');
              }, 0);
            },
            onError: () => {
              setDisplayToast(true);
            },
          }
        );
      }
    },
    [
      buildCommunicationDataRequest,
      bulkCommunicationReport,
      convertBulkReportRequestToPascalCase,
      dispatch,
      navigate,
      reportIDFromUrl,
    ]
  );

  // Set the ref after handleMenuAction is defined
  useEffect(() => {
    handleMenuActionRef.current = handleMenuAction;
  }, [handleMenuAction]);

  return (
    <Box className="communication-explorer-container communication-wrapper-padding">
      <CommunicationsExplorerHeader
        hasSelectedSources={hasSelectedSources}
        reportCriteria={reportCriteriaForSave}
        dataAsOf={dataAsOfForSave}
        reportId={reqData?.reportId || 0}
        onShareReport={handleShareReport}
        reportTitle={reportTitle}
      />
      <ArrAccordionWithCheckbox
        expandedSection={'filterHeader'}
        sections={sections}
      />
      {(!reqData && !reportIDFromUrl) || (!reqData && reportTitle) ? (
        <NoReport />
      ) : (
        <BubblesAndLinks
          svgRef={svgRef}
          reqData={reqData}
          showBy={showBy}
          setCanDownload={setCanDownload}
          onNodeSelectionChange={handleNodeSelectionChange}
          onLinkSelectionChange={handleLinkSelectionChange}
          onDataLoad={handleDataLoad}
          onReportDataLoad={handleReportDataLoad}
          selectedNodeIds={selectedNodeIds}
          selectedLinkIds={selectedLinkIds}
          actionMenuItems={actionMenuItems}
          sourceDetails={getSourceDetailsForActionMenu}
          getDoActionData={getDoActionData}
          getSourceDetails={buildSourceDetails}
          showToastFromParent={showToastFromParent}
          onActionPick={handleActionPick}
          validateBeforeBulkAction={validateBeforeBulkAction}
          isMenuDisabled={isMenuDisabled}
        />
      )}
      {displayToast && (
        <ArrToastNotification
          message={t(`common.noDataAvailable`)}
          status={[ToastType.Error]}
          onClose={() => setDisplayToast(false)}
        />
      )}

      {/* Bulk Tag Modal */}
      {isBulkTagAction && masterDocListId > 0 && (
        <BulkTagUntag
          isOpen={isBulkTagAction}
          setOpenActionMenu={setIsBulkTagAction}
          handleBulkTagContinue={handleBulkTagContinue}
          masterDocListId={masterDocListId}
          setSelectedNewExistingTag={setSelectedNewExistingTag}
          isNonbg={false}
        />
      )}

      {/* Bulk Folder Modal */}
      {isBulkFolderAction && masterDocListId > 0 && (
        <BulkFolderUnFolder
          data-testid="bulk-folder-unfolder"
          isOpen={isBulkFolderAction}
          setOpenActionMenu={(value) => {
            if (!value) setIsBulkFolderAction(false);
          }}
          masterDocListId={masterDocListId}
          setSelectedNewExistingFolder={setSelectedNewExistingFolder}
          handleBulkFolderContinue={handleBulkFolderContinue}
        />
      )}

      {/* Bulk Release Modal */}
      {isBulkReleaseAction && (
        <BulkReleaseFlyout
          isOpen={isBulkReleaseAction}
          onClose={() => {
            setIsBulkReleaseAction(false);
          }}
          onRelease={handleBulkRelease}
          onUnrelease={handleBulkUnrelease}
        />
      )}

      {/* Bulk Assign Modal */}
      {isBulkAssignAction && (
        <BulkAssignFlyout
          isOpen={isBulkAssignAction}
          onClose={() => {
            setIsBulkAssignAction(false);
            samplingMethodForAssign.current = 'count';
            countForAssign.current = 1;
            percentForAssign.current = 1;
            persistContentForAssign.current = false;
          }}
          onContinue={handleBulkAssignContinue}
          selectedIds={[]}
          masterDocListId={masterDocListId}
          onTreeSelect={(ids) => setSelectedTreeIds(ids ?? [])}
        />
      )}

      {/* Loading Spinner */}
      {(isBulkActionsPopupLoading ||
        isCommunicationDataLoading ||
        isBulkReportLoading ||
        isNavigatingToDocList ||
        isNavigatingToDocView) && (
        <ArrLoadingSpinner
          overlay
          isOpen={
            isBulkActionsPopupLoading ||
            isCommunicationDataLoading ||
            isBulkReportLoading ||
            isNavigatingToDocList ||
            isNavigatingToDocView
          }
        />
      )}

      {/* Document Limit Warning Modal */}
      <ArrDocMaxLimitWarning
        totalDocumentCount={documentCountFromAPI}
        show={showDocLimitWarning}
        destination={navigationType}
        onCancel={() => setShowDocLimitWarning(false)}
        onContinue={(totalDocumentToView: number) =>
          startDocumentNavigation(selectedActionId, totalDocumentToView)
        }
      />

      {/* Action Like Documents Modal */}
      {isActionLikeModal && (
        <ActionLikeDocuments
          data-testid="action-like"
          selectedIds={[]}
          showModal={!!isActionLikeModal}
          onClose={() => {
            setIsActionLikeModal(false);
            setActionLikeChangeList(undefined);
            if (selectedNewExistingTag?.type === 'new') {
              setIsBulkTagAction(false);
            }
            if (selectedNewExistingFolder?.type === 'new') {
              setIsBulkFolderAction(false);
            }
            if (isActionLikeModal === 'bulkRelease') {
              setIsBulkReleaseAction(false);
            }
            if (isActionLikeModal === 'bulkAssign') {
              if (assignmentSelectedTab.current === 'new') {
                setIsBulkAssignAction(false);
                assignmentSelectedTab.current = 'existing';
                setSelectedTreeIds([]);
                setBulkAssignmentId(0);
              }
              samplingMethodForAssign.current = 'count';
              countForAssign.current = 1;
              percentForAssign.current = 1;
              persistContentForAssign.current = false;
            }
          }}
          onCancel={() => {
            setIsActionLikeModal(false);
            setActionLikeChangeList(undefined);
            if (selectedNewExistingTag?.type === 'new') {
              setIsBulkTagAction(false);
            }
            if (selectedNewExistingFolder?.type === 'new') {
              setIsBulkFolderAction(false);
            }
            if (isActionLikeModal === 'bulkRelease') {
              setIsBulkReleaseAction(false);
            }
            if (isActionLikeModal === 'bulkAssign') {
              if (assignmentSelectedTab.current === 'new') {
                setIsBulkAssignAction(false);
                assignmentSelectedTab.current = 'existing';
                setSelectedTreeIds([]);
                setBulkAssignmentId(0);
              }
              samplingMethodForAssign.current = 'count';
              countForAssign.current = 1;
              percentForAssign.current = 1;
              persistContentForAssign.current = false;
            }
          }}
          onContinue={handleActionLike}
          continueButtonLabel={t(
            `docExplorer.actionMenu.actionLikeDocuments.finalize.${
              isActionLikeModal === 'bulkTag'
                ? 'bulkTag'
                : isActionLikeModal === 'bulkRelease'
                  ? 'bulkRelease'
                  : isActionLikeModal === 'bulkAssign'
                    ? 'bulkAssign'
                    : isActionLikeModal === 'bulkFolder'
                      ? 'bulkFolder'
                      : ''
            }`
          )}
          onActionChange={onActionLikeChange}
          masterDocListId={masterDocListId}
          analyticsIndicatorStatus={0}
          actionType={
            isActionLikeModal === 'bulkTag'
              ? 'TAG'
              : isActionLikeModal === 'bulkRelease'
                ? 'RELEASE'
                : isActionLikeModal === 'bulkAssign'
                  ? 'ASSIGNMENT'
                  : isActionLikeModal === 'bulkFolder'
                    ? 'FOLDER'
                    : undefined
          }
          actionList={initialActionList}
        />
      )}

      {/* Bulk Tagging Warning Modal */}
      {isBulkWarning && (
        <BulkTaggingWarning
          onClose={() => {
            setIsBulkWarning(false);
            setActionLikeChangeList(undefined);
            if (selectedNewExistingTag?.type === 'new') {
              setIsBulkTagAction(false);
            }
          }}
          showModal={isBulkWarning}
          sourceType={SourceTypeCode.COMMUNICATIONMAP as any}
          actionLikeChangeList={actionLikeChangeList}
          masterDocListId={Number(masterDocListId)}
          selectedNewExistingTag={getSelectedNewExistingTag()}
          buttons={[
            {
              id: '1',
              label: t('common.cancel'),
              onClick: () => {
                setIsBulkWarning(false);
                setActionLikeChangeList(undefined);
                if (selectedNewExistingTag?.type === 'new') {
                  setIsBulkTagAction(false);
                }
              },
              variant: 'secondary',
            },
            {
              id: '2',
              label: t('common.ok'),
              onClick: handleBulkWarning,
              variant: 'primary',
            },
          ]}
        />
      )}
    </Box>
  );
};

export default CommunicationExplorer;
